import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useCmsContent } from '@/hooks/useCmsContent';

const features = [
  { key: 'werk_feature_1', default: 'Onderhoud' },
  { key: 'werk_feature_2', default: 'Diagnose' },
  { key: 'werk_feature_3', default: 'Montage' },
  { key: 'werk_feature_4', default: 'Custom werk' },
];

export default function WerkplaatsHero() {
  const { getContent } = useCmsContent('werkplaats');
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    const tryPlay = () => video.play().catch(() => {});
    tryPlay();
    const forcePlay = () => { tryPlay(); document.removeEventListener('touchstart', forcePlay); };
    document.addEventListener('click', forcePlay, { once: true });
    document.addEventListener('touchstart', forcePlay, { once: true });
    return () => { document.removeEventListener('touchstart', forcePlay); };
  }, []);

  const handleScroll = useCallback(() => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const sectionHeight = sectionRef.current.offsetHeight;
    const triggerStart = 0;
    const triggerEnd = sectionHeight * 0.6;
    const scrolled = Math.max(0, -rect.top);
    const progress = Math.min(1, Math.max(0, (scrolled - triggerStart) / (triggerEnd - triggerStart)));
    setScrollProgress(progress);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const basePadding = 16;
  const currentPadding = basePadding * (1 - scrollProgress);
  const currentRadius = 24 * (1 - scrollProgress);
  const contentOpacity = 1 - scrollProgress * 1.5;

  return (
    <section ref={sectionRef} className="pt-[82px]" style={{ paddingBottom: `${Math.max(0, 8 * (1 - scrollProgress))}px` }}>
      <div
        className="relative w-full mx-auto min-h-[400px] md:min-h-[600px] xl:min-h-[700px] overflow-hidden transition-[border-radius] duration-100"
        style={{
          height: 'calc(100vh - 120px)',
          marginLeft: `${currentPadding}px`,
          marginRight: `${currentPadding}px`,
          width: `calc(100% - ${currentPadding * 2}px)`,
          borderRadius: `${currentRadius}px`,
        }}
      >
        {/* Video background */}
        <motion.div
          initial={{ scale: 1.05 }}
          animate={{ scale: 1 }}
          transition={{ duration: 10, ease: 'easeOut' }}
          className="absolute inset-0"
        >
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          >
            <source src="https://lozejxkhsvwkksgzhrfd.supabase.co/storage/v1/object/public/images/Vermado%20automotive%20website%20banner%20video%20(1).mp4" type="video/mp4" />
          </video>
        </motion.div>

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

        {/* Main content */}
        <div
          className="relative z-20 h-full flex flex-col justify-end pb-8 md:pb-12 lg:pb-16 px-8 md:px-12 lg:px-16"
          style={{ opacity: Math.max(0, contentOpacity) }}
        >
          <div className="max-w-xl">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 md:mb-6 leading-tight"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              <span>{getContent('hero_headline', 'WERKPLAATS')}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-white/90 text-base md:text-lg mb-4 max-w-lg leading-relaxed"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              <span>{getContent('hero_description', 'Ervaar het verschil met onze premium service en expertise. Voor al uw onderhoud en custom werkzaamheden.')}</span>
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-white text-lg md:text-xl mb-8"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              <span>{getContent('hero_accent_before', 'Prestatie geeft ')}</span>
              <span className="relative inline-block">
                <span className="absolute inset-0 bg-[#B1A06D]" style={{ transform: 'skewX(-4deg) rotate(-2deg)', borderRadius: '4px' }} />
                <span className="relative z-10 text-black px-2 py-0.5 font-semibold inline-block">
                  <span>{getContent('hero_accent_highlight', 'vrijheid')}</span>
                </span>
              </span>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row sm:items-center gap-6"
            >
              <motion.button
                onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 bg-white text-black px-6 py-4 rounded-xl font-semibold text-sm uppercase tracking-wider hover:bg-white/95 transition-colors"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                <span>{getContent('hero_button_text', 'Bekijk onze werkplaats')}</span>
              </motion.button>
              <div className="flex items-center gap-3 text-white/80 text-sm">
                <span className="font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  <span>{getContent('hero_trust_text', 'Professionele automotive service')}</span>
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Feature bar */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="absolute bottom-8 md:bottom-12 right-8 md:right-12 lg:right-16 z-20 hidden md:block"
          style={{ opacity: Math.max(0, contentOpacity) }}
        >
          <div className="bg-black/70 backdrop-blur-sm rounded-xl px-6 py-4 flex flex-wrap gap-x-8 gap-y-2 items-center">
            {features.map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#B1A06D] flex-shrink-0" />
                <span className="text-white text-sm font-medium">{getContent(f.key, f.default)}</span>
              </div>
            ))}
          </div>
        </motion.div>

      </div>
    </section>
  );
}
